// Simple in-memory cache with localStorage persistence
class DataCache {
  constructor() {
    this.cache = new Map();
    this.ttl = new Map();
    this.defaultTTL = 5 * 60 * 1000; // 5 minutes default
    this.loadFromStorage();
  }

  // Load cache from localStorage
  loadFromStorage() {
    try {
      const cached = localStorage.getItem('zedscholar_cache');
      if (cached) {
        const data = JSON.parse(cached);
        this.cache = new Map(data.cache || []);
        this.ttl = new Map(data.ttl || []);
        
        // Clean expired entries
        this.cleanExpired();
      }
    } catch (error) {
      console.warn('Failed to load cache from storage:', error);
    }
  }

  // Save cache to localStorage
  saveToStorage() {
    try {
      const data = {
        cache: Array.from(this.cache.entries()),
        ttl: Array.from(this.ttl.entries())
      };
      localStorage.setItem('zedscholar_cache', JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save cache to storage:', error);
    }
  }

  // Clean expired entries
  cleanExpired() {
    const now = Date.now();
    for (const [key, expiry] of this.ttl.entries()) {
      if (now > expiry) {
        this.cache.delete(key);
        this.ttl.delete(key);
      }
    }
  }

  // Set cache value
  set(key, value, ttl = this.defaultTTL) {
    this.cache.set(key, value);
    this.ttl.set(key, Date.now() + ttl);
    this.saveToStorage();
  }

  // Get cache value
  get(key) {
    this.cleanExpired();
    return this.cache.get(key);
  }

  // Check if key exists and is not expired
  has(key) {
    this.cleanExpired();
    return this.cache.has(key);
  }

  // Delete specific key
  delete(key) {
    this.cache.delete(key);
    this.ttl.delete(key);
    this.saveToStorage();
  }

  // Clear all cache
  clear() {
    this.cache.clear();
    this.ttl.clear();
    this.saveToStorage();
  }

  // Get cache size
  size() {
    this.cleanExpired();
    return this.cache.size;
  }

  // Force refresh (delete and return true if existed)
  forceRefresh(key) {
    const existed = this.has(key);
    if (existed) {
      this.delete(key);
    }
    return existed;
  }
}

// Create singleton instance
const dataCache = new DataCache();

export default dataCache;
